import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Loader from 'src/components/loader';
import AddNewEvent from '../components/AddNewEvent';
import { RootState } from 'src/store/reducers/state';
import { useNavigate } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import Pagination from 'src/components/common/pagination';
import { useEventActions } from 'src/store/event/actions';
import { AddEventModal } from 'src/models/eventModel';
import { DebounceInput } from 'react-debounce-input';
import { minutesToHourMinutes } from 'src/components/utilities/utils';
import moment from 'moment';
import { successMsg } from 'src/components/utilities/utils';
import ToastMessage from 'src/components/ToastContainer';
import DeleteEventModal from '../components/DeleteEventModal';

type EventTab = 'upcoming' | 'past';
const getDateRangeForTab = (tab: EventTab) => {
  const cutoff = moment().subtract(1, 'day').startOf('day').toISOString();
  if (tab === 'upcoming') {
    return { fromDate: cutoff, toDate: undefined };
  }
  return { fromDate: undefined, toDate: cutoff };
};
const Event: React.FC = () => {
  interface EventFilterProp {
    title?: string;
    name?: string;
    pageNumber: number | string;
    pageSize?: number | string;
    sortFieldName?: string;
    isAscending?: boolean;
    fromDate?: string;
    toDate?: string;
  }
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const ref = useRef<any>(null);
  const [rightClickEventId, setRightClickEventId] = useState('');
  const eventAction = useEventActions(dispatch);
  const [activeTab, setActiveTab] = useState<EventTab>('upcoming');
  const [filter, setFilter] = useState<EventFilterProp>({
    pageNumber: 1,
    pageSize: 25,
    sortFieldName: 'eventdate',
    isAscending: false,
    ...getDateRangeForTab('upcoming')
  });
  const [searchValue, setSearchValue] = useState<string>('');
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [dateRange, setDateRange] = useState<{ from: string; to: string }>({ from: '', to: '' });
  const filterRef = useRef<HTMLDivElement>(null);
  const [eventId, setEventId] = useState<string>('');
  const { events, isEventSaved, isEventDeleted, paginationMeta, loader } = useSelector(
    (state: RootState) => {
      return {
        events: state.event.events,
        isEventSaved: state.event.isEventSaved,
        isEventDeleted: state.event.isEventDeleted,
        paginationMeta: state.event.paginationMeta,
        loader: state.event.loading
      };
    }
  );
  const handleAction = (id: string, type: string, eventUrl: string) => {
    const url = eventUrl;
    if (type == 'edit') window.open(`/event/edit/${id}`, '_blank');
    else if (type == 'registeredusers') navigate(`/event/registerusers/${id}`);
    else if (type == 'delete') return true;
    else if (type == 'launch') window.open(url, '_blank');
    else return false;
  };
  const handlePageChange = (pageNumber: number) => {
    setFilter({
      ...filter,
      pageNumber,
      pageSize: 25,
      name: searchValue
    });
  };
  const copyTextToClipboard = (eventUrl: string) => {
    if ('clipboard' in navigator) {
      navigator.clipboard.writeText(`${eventUrl}`);
      successMsg('Url copied to clipboard!');
    } else {
      document.execCommand('copy', true, `${eventUrl}`);
    }
  };

  useEffect(() => {
    eventAction.getEventRequest(filter);
  }, [isEventDeleted, filter, isEventSaved]);

  const handleSearchVideo = (e: string) => {
    setSearchValue(e);
    eventAction.getEventRequest({
      pageNumber: 1,
      pageSize: 25,
      name: e,
      ...getDateRangeForTab(activeTab)
    });
  };

  const handleDuration = (value: string, duration: number) => {
    if (value.includes('.')) {
      return moment.duration(duration, 'minutes').days();
    } else {
      return minutesToHourMinutes(duration);
    }
  };
  const openInNewTabClick = () => {
    handleResetRightClick();
  };

  const handleRightClick = (e: any, eventId: string) => {
    e.preventDefault();
    setRightClickEventId(eventId);
  };
  useEffect(() => {
    if (ref.current) {
      ref.current.click();
    }
  }, [rightClickEventId]);

  const handleResetRightClick = () => {
    setRightClickEventId('');
  };

  const handleDeleteEventClick = (id: string) => {
    setEventId(id);
  };
  useEffect(() => {
    if (isEventDeleted) {
      successMsg('success');
    }
  }, [isEventDeleted]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) {
        setShowFilterPanel(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);


  const handleTabChange = (tab: EventTab) => {
    if (tab === activeTab) return;
    setActiveTab(tab);
    setSearchValue('');
    setDateRange({ from: '', to: '' });
    setFilter({
      ...filter,
      pageNumber: 1,
      name: '',
      ...getDateRangeForTab(tab),
    });
  };
  const handleApplyDateFilter = () => {
    setFilter({
      ...filter,
      pageNumber: 1,
      fromDate: dateRange.from ? moment(dateRange.from).startOf('day').toISOString() : undefined,
      toDate: dateRange.to ? moment(dateRange.to).endOf('day').toISOString() : undefined,
    });
    setShowFilterPanel(false);
  };

  const handleClearDateFilter = () => {
    setDateRange({ from: '', to: '' });
    setFilter({
      ...filter,
      pageNumber: 1,
      ...getDateRangeForTab(activeTab),
    });
    setShowFilterPanel(false);
  };
  const productInformation = localStorage.getItem('productInformation');
  const parsedProduct = productInformation && JSON.parse(productInformation);
  const isEventsAllowed = parsedProduct?.events !== false;

  const currentTabCount = paginationMeta?.totalCount ?? events?.length ?? 0;

  return (
    <>
      <ToastMessage />
      {!isEventsAllowed && (
        <>
          <div className=" inset-0 flex items-center justify-center bg-gray-200 bg-opacity-10">
            <div className="bg-white rounded-lg shadow-lg p-6 w-[360px] text-center relative">
              <h2 className="text-gray-900 font-semibold text-lg mb-2">
                This feature isn’t included in your current plan.
              </h2>
              <p className="text-gray-600 text-sm mb-5">
                Contact Sales to upgrade your plan and unlock access.
              </p>
              <button
                className="bg-purple-700 hover:bg-purple-800 text-white font-semibold px-5 py-2 rounded-md"
                onClick={() => {
                  window.open('https://support.webcastcloud.com/hc/en-us/requests/new', '_blank');
                }}>
                Contact Sales
              </button>
            </div>
          </div>
        </>
      )}
      <div
        className={`mt-4 members-list ${!isEventsAllowed ? 'opacity-40 pointer-events-none' : ''}`}>
        <div>
          <div className="flex flex-col justify-center h-full gap-4">
            {/* Page header */}
            <div className="md:flex justify-between items-start">
              <div>
                <h2 className="font-bold text-2xl text-gray-900">Events</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Create, manage and monitor your upcoming and past events.
                </p>
              </div>
              <div className="lg:flex items-center text-center mt-3 md:mt-0">
                <button
                  type="button"
                  data-bs-toggle="modal"
                  data-bs-target="#AddNewEvent"
                  className="btn-primary w-auto mt-0 mr-0 ml-auto mb-0 px-4 py-2 rounded-lg flex items-center gap-2">
                  <span className="text-base leading-none">+</span> Add Event
                </button>
              </div>
            </div>

            {/* <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white shadow-card rounded-lg p-4 flex items-center gap-3">
                <div className="w-11 h-11 rounded-lg bg-purple-100 text-purple-700 flex items-center justify-center shrink-0">
                  <i className="fas fa-calendar-alt"></i>
                </div>
                <div className="text-left">
                  <div className="text-sm text-gray-500">
                    {activeTab === 'upcoming' ? 'Upcoming Events' : 'Past Events'}
                  </div>
                  <div className="text-2xl font-bold text-gray-900">
                    {activeTab === 'upcoming' ? '8' : '28'}
                  </div>
                  <div className="text-xs text-gray-400">
                    {activeTab === 'upcoming' ? 'In the next 90 days' : 'All time'}
                  </div>
                </div>
              </div>
              <div className="bg-white shadow-card rounded-lg p-4 flex items-center gap-3">
                <div className="w-11 h-11 rounded-lg bg-green-100 text-green-700 flex items-center justify-center shrink-0">
                  <i className="fa fa-users"></i>
                </div>
                <div className="text-left">
                  <div className="text-sm text-gray-500">Total Registrations</div>
                  <div className="text-2xl font-bold text-gray-900">124</div>
                  <div className="text-xs text-gray-400">Across upcoming events</div>
                </div>
              </div>
              <div className="bg-white shadow-card rounded-lg p-4 flex items-center gap-3">
                <div className="w-11 h-11 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center shrink-0">
                  <i className="fa fa-clock"></i>
                </div>
                <div className="text-left">
                  <div className="text-sm text-gray-500">Total Duration</div>
                  <div className="text-2xl font-bold text-gray-900">08:30:00</div>
                  <div className="text-xs text-gray-400">Hours</div>
                </div>
              </div>
              <div className="bg-white shadow-card rounded-lg p-4 flex items-center gap-3">
                <div className="w-11 h-11 rounded-lg bg-orange-100 text-orange-700 flex items-center justify-center shrink-0">
                  <i className="fa fa-video"></i>
                </div>
                <div className="text-left">
                  <div className="text-sm text-gray-500">Live Events</div>
                  <div className="text-2xl font-bold text-gray-900">5</div>
                  <div className="text-xs text-gray-400">Scheduled live</div>
                </div>
              </div>
            </div> */}

            <div className="w-full mx-auto bg-white shadow-card rounded-lg relative" style={{ minHeight: `${showFilterPanel ? '300px' : '0px'}` }}>
              {/* Tabs + Search + Filter */}
              <div className="px-5 pt-4 md:flex items-center justify-between border-b border-gray-100">
                <div className="flex items-center gap-2 mb-3 md:mb-0">
                  <button
                    disabled={loader}
                    type="button"
                    onClick={() => handleTabChange('upcoming')}
                    className={`pb-3 px-1 text-sm font-semibold border-b-2 transition-colors flex items-center gap-2 mr-4 ${activeTab === 'upcoming'
                      ? 'border-purple-700 text-purple-700'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}>
                    Upcoming Events
                    <span
                      className={`text-xs font-semibold rounded-full px-2 py-0.5 ${activeTab === 'upcoming'
                        ? 'bg-purple-100 text-purple-700'
                        : 'bg-gray-100 text-gray-500'
                        }`}>
                      {activeTab === 'upcoming' ? currentTabCount : ''}
                    </span>
                  </button>
                  <button
                    disabled={loader}
                    type="button"
                    onClick={() => handleTabChange('past')}
                    className={`pb-3 px-1 text-sm font-semibold border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'past'
                      ? 'border-purple-700 text-purple-700'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}>
                    Past Events
                    <span
                      className={`text-xs font-semibold rounded-full px-2 py-0.5 ${activeTab === 'past'
                        ? 'bg-purple-100 text-purple-700'
                        : 'bg-gray-100 text-gray-500'
                        }`}>
                      {activeTab === 'past' ? currentTabCount : ''}
                    </span>
                  </button>
                </div>

                <div className="flex items-center gap-3 pb-3">
                  <div className="form-group flex items-center text-center mb-0 relative">
                    <DebounceInput
                      minLength={2}
                      className="input-style w-52"
                      placeholder="Search events..."
                      debounceTimeout={500}
                      value={searchValue}
                      onChange={(event) => handleSearchVideo(event.target.value)}
                    />
                    <i className="fa fa-search absolute right-3 text-gray-400 text-xs"></i>
                  </div>
                  <div className="relative" ref={filterRef}>
                    <button
                      type="button"
                      onClick={() => setShowFilterPanel((prev) => !prev)}
                      className="border border-gray-200 rounded-lg px-3 py-2 text-sm font-semibold text-gray-600 flex items-center gap-2 hover:bg-gray-50">
                      <i className="fa fa-filter text-xs"></i> Filter
                    </button>

                    {showFilterPanel && (
                      <div className="absolute right-0 mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-10">
                        <div className="text-sm font-semibold text-gray-700 mb-3">Filter by date range</div>
                        <div className="mb-3">
                          <label className="text-xs text-gray-500 mb-1 block">From</label>
                          <input
                            type="date"
                            className="input-style w-full"
                            value={dateRange.from}
                            max={dateRange.to || undefined}
                            onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
                          />
                        </div>
                        <div className="mb-4">
                          <label className="text-xs text-gray-500 mb-1 block">To</label>
                          <input
                            type="date"
                            className="input-style w-full"
                            value={dateRange.to}
                            min={dateRange.from || undefined}
                            onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
                          />
                        </div>
                        <div className="flex justify-between gap-2">
                          <button
                            type="button"
                            onClick={handleClearDateFilter}
                            className="text-sm font-semibold text-gray-500 hover:text-gray-700 px-3 py-2">
                            Clear
                          </button>
                          <button
                            type="button"
                            onClick={handleApplyDateFilter}
                            disabled={!dateRange.from && !dateRange.to}
                            className="bg-purple-700 hover:bg-purple-800 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold px-4 py-2 rounded-md">
                            Apply
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="p-3">
                <div className="overflow-x-auto-sm">
                  <table className="table-auto w-full border">
                    <thead className="text-xs font-semibold uppercase bg-gray-50">
                      <tr>
                        <th className="p-2">
                          <div className="font-semibold text-left">Event Name</div>
                        </th>
                        <th className="p-2">
                          <div className="font-semibold text-left">Date &amp; Time</div>
                        </th>
                        <th className="p-2">
                          <div className="font-semibold text-left">Timezone</div>
                        </th>
                        <th className="p-2">
                          <div className="font-semibold text-left">Duration(hh:mm / days)</div>
                        </th>
                        <th className="p-2">
                          <div className="font-semibold text-left">Registrations</div>
                        </th>
                        <th className="p-2">
                          <div className="font-semibold text-left">Event Type</div>
                        </th>
                        <th className="p-2">
                          <div className="font-semibold text-left">Status</div>
                        </th>
                        <th className="p-2">
                          <div className="font-semibold text-left">Actions</div>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="text-sm divide-y divide-gray-100">
                      {loader ? (
                        <tr>
                          <td>
                            <Loader />
                          </td>
                        </tr>
                      ) : (
                        <>
                          {events && events?.length ? (
                            <>
                              {events &&
                                events.map((event, index) => {
                                  return (
                                    <tr
                                      onContextMenu={(e) => handleRightClick(e, event.id)}
                                      key={index}>
                                      <td className="p-2">
                                        <div className="flex items-center gap-3">
                                          <div className="w-8 h-8 rounded-md bg-purple-50 text-purple-600 flex items-center justify-center shrink-0">
                                            <i className="fas fa-calendar-alt text-xs"></i>
                                          </div>
                                          <div className="text-left">
                                            <div className="font-medium text-gray-900">
                                              {event.name}
                                            </div>
                                            {event.title && (
                                              <div className="text-xs text-gray-500">
                                                {event.title}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </td>
                                      <td className="p-2">
                                        <div className="text-left">
                                          <div className="text-gray-900">{event.displayDate}</div>
                                          <div className="text-xs text-gray-500">
                                            {event.displayTime}
                                          </div>
                                        </div>
                                      </td>
                                      <td className="p-2">
                                        <div className="text-left">{event.timezoneAbbv}</div>
                                      </td>
                                      <td className="p-2">
                                        <div className="text-left">
                                          {handleDuration(event.displayDuration, event.duration)}
                                        </div>
                                      </td>
                                      <td className="p-2">
                                        <div className="text-left">{event.noOfRegistrations}</div>
                                      </td>
                                      <td className="p-2">
                                        <div className="text-left">
                                          <span className="inline-block text-xs font-semibold bg-purple-50 text-purple-700 rounded-full px-2 py-1">
                                            {event.displayEventType}
                                          </span>
                                        </div>
                                      </td>
                                      <td className="p-2">
                                        <div className="text-left">
                                          <span className="inline-block text-xs font-semibold bg-green-50 text-green-700 rounded-full px-2 py-1">
                                            {activeTab === 'upcoming' ? 'Scheduled' : 'Completed'}
                                          </span>
                                        </div>
                                      </td>
                                      {rightClickEventId === event.id ? (
                                        <td className="text-left">
                                          <div>
                                            <div className="flex justify-center">
                                              <div>
                                                <div className="dropdown relative">
                                                  <a
                                                    ref={ref}
                                                    className="
                                                 dropdown-toggle
                                                 transition
                                                 duration-150
                                                 ease-in-out"
                                                    href="#"
                                                    type="button"
                                                    id="ActionButton"
                                                    data-bs-toggle="dropdown"
                                                    aria-expanded="false">
                                                    <i className="fas fa-ellipsis-v"></i>
                                                  </a>
                                                  <ul
                                                    className="dropdown-menu action-dropdown"
                                                    aria-labelledby="ActionButton">
                                                    <li
                                                      onClick={() => {
                                                        openInNewTabClick();
                                                        handleAction(
                                                          event.id,
                                                          'edit',
                                                          event.eventUrl
                                                        );
                                                      }}>
                                                      <span className="dropdown-item flex-items cursor-pointer">
                                                        <i className="fas fa-edit w-6"></i>
                                                        <span>Open in new tab</span>
                                                      </span>
                                                    </li>
                                                  </ul>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        </td>
                                      ) : (
                                        <td className="p-2">
                                          <div className="text-left flex items-center gap-3">
                                            <button
                                              type="button"
                                              title="Edit"
                                              onClick={() => {
                                                handleAction(event.id, 'edit', event.eventUrl);
                                              }}
                                              className="text-gray-400 hover:text-purple-700">
                                              <i className="fas fa-edit"></i>
                                            </button>
                                            <div className="dropdown relative">
                                              <button
                                                className="dropdown-toggle text-gray-400 hover:text-gray-700"
                                                type="button"
                                                id="action-dropdown"
                                                data-bs-toggle="dropdown"
                                                aria-expanded="false">
                                                <i className="fas fa-ellipsis-h"></i>
                                              </button>
                                              <ul
                                                className="dropdown-menu dropdown-menu-ul"
                                                aria-labelledby="action-dropdown">
                                                <li>
                                                  <button
                                                    type="button"
                                                    onClick={() => {
                                                      handleAction(
                                                        event.id,
                                                        'edit',
                                                        event.eventUrl
                                                      );
                                                    }}
                                                    className="action-menu flex items-center">
                                                    <i className="fas fa-edit w-6 text-left"></i>
                                                    <span>Edit</span>
                                                  </button>
                                                </li>
                                                <li>
                                                  <button
                                                    type="button"
                                                    onClick={() => {
                                                      handleAction(
                                                        event.id,
                                                        'launch',
                                                        event.eventUrl
                                                      );
                                                    }}
                                                    className="action-menu flex items-center">
                                                    <i className="fa fa-play w-6 text-left"></i>
                                                    <span>Launch</span>
                                                  </button>
                                                </li>
                                                <li>
                                                  <button
                                                    type="button"
                                                    onClick={() => {
                                                      handleAction(
                                                        event.id,
                                                        'registeredusers',
                                                        event.eventUrl
                                                      );
                                                    }}
                                                    className="action-menu flex items-center">
                                                    <i className="fa fa-users w-6 text-left"></i>
                                                    <span>Registered Users</span>
                                                  </button>
                                                </li>
                                                <li>
                                                  <button
                                                    type="button"
                                                    onClick={() => {
                                                      copyTextToClipboard(event.eventUrl);
                                                    }}
                                                    className="action-menu flex items-center">
                                                    <i className="fa fa-link w-6 text-left"></i>
                                                    <span>Copy Link</span>
                                                  </button>
                                                </li>
                                                <li
                                                  data-bs-toggle="modal"
                                                  data-bs-target="#DeleteEventModal"
                                                  onClick={() => handleDeleteEventClick(event.id)}>
                                                  <span className="action-menu flex items-center cursor-pointer text-red-500">
                                                    <i className="fas fa-trash w-6"></i>
                                                    <span>Delete</span>
                                                  </span>
                                                </li>
                                              </ul>
                                            </div>
                                          </div>
                                        </td>
                                      )}
                                    </tr>
                                  );
                                })}
                            </>
                          ) : (
                            <tr>
                              <td>No Data Found</td>
                            </tr>
                          )}
                        </>
                      )}
                    </tbody>
                  </table>
                  {!!paginationMeta && !!Object.keys(paginationMeta)?.length && (
                    <Pagination
                      currentPage={paginationMeta.pageNumber}
                      totalCount={paginationMeta.totalCount}
                      pageSize={paginationMeta.pageSize}
                      onPageChange={(page: any) => handlePageChange(page)}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Modals   */}
        <AddNewEvent eventName="" />
        <DeleteEventModal eventId={eventId} />
      </div>
    </>
  );
};

export default Event;